// core
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

// campfire
import { LoggerService, PartnerBrandService, IntercomService } from '@campfire-nx/api';

// services
import { PolicyDetailsService } from '@open-portal-app/core/services/policy-details.service';
import { AuthService } from '@open-portal-app/core/services/auth.service';
import { UserDataService } from '@open-portal-app/core/services/user-data.service';
import { environment } from '@open-portal-env';
import { TpaService } from '@open-portal-app/core/services/tpa.service';

// constants
import { HELP_CONSTANTS } from '@open-portal-app/help/constants/help.constants';
import { POLICY_DETAIL_CONFIG } from '@open-portal-app/core/constants/policy.constants';

// interfaces
import { IConfigInterface } from '../components/policy-link-cards/policy-link-cards.component';

@Component({
  selector: 'policy-details',
  templateUrl: './policy-details.component.html',
  styleUrls: ['./policy-details.component.scss']
})
export class PolicyDetailsComponent implements OnInit {
  userDataLoading = false;
  isLoadingError = false;
  token!: string;
  backendUrl = environment.apiEndpoint;
  policyDetails!: any;
  claimsData!: any;
  policyDisplayTitle!: string;
  selfService!: string;
  source!: string;
  initialTabState!: number;
  badgeStatus = HELP_CONSTANTS.policyBadgeStatus as any;
  claimsActiveConfig = POLICY_DETAIL_CONFIG.claimsActiveConfig as IConfigInterface;
  claimsCompletedConfig = POLICY_DETAIL_CONFIG.claimsCompletedConfig as IConfigInterface;
  policyDocumentConfig = POLICY_DETAIL_CONFIG.policyDocumentConfig as IConfigInterface;
  policyContactConfig = POLICY_DETAIL_CONFIG.policyContactConfig as IConfigInterface;
  extrasActiveConfig = POLICY_DETAIL_CONFIG.extrasActiveConfig as IConfigInterface;
  policyMobileExcessConfig = POLICY_DETAIL_CONFIG.policyMobileExcessConfig as IConfigInterface;
  userData!: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private partnerBrandService: PartnerBrandService,
    private policyDetailsService: PolicyDetailsService,
    private authService: AuthService,
    private userDataService: UserDataService,
    private loggerService: LoggerService,
    private intercomService: IntercomService,
    private tpaService: TpaService
  ) {}

  intercomData = this.partnerBrandService.getAttribute('intercom', 'partner');
  isIntercomEnabled = this.intercomData?.intercomAppId;

  async ngOnInit(): Promise<void> {
    this.token = encodeURIComponent(this.authService.getToken() as string);
    this.userData = await this.userDataService.getUserData();
    this.route.queryParams
      .subscribe(params => {
        this.selfService = params.selfService;
        this.source = params.source;
      });

    if (this.route.snapshot.data.policy) {
      this.userDataLoading = true;
      let currentEventLogic = 'setPolicyDetails';

      this.setPolicyDetails()
        .then(() => {
          currentEventLogic = 'setPolicyDisplayLabel';
          this.userDataLoading = false;
        })
        .catch((err) => {
          this.userDataLoading = false;
          this.isLoadingError = true;
          this.loggerService.error('PolicyDetailsComponent', 'ngOnInit', { error: err }, currentEventLogic);
        });
    }
  }

  setPolicyDetails() {
    return this.policyDetailsService.formatPolicy(undefined, this.userData).then((response: any) => {
      this.policyDetails = response;
    });
  }

  goBack() {
    this.router.navigate([this.source === 'help_flow' ? '/support' : '/dashboard']);
  }

  retryPayment() {
    this.loggerService.info('PolicyDetailsComponent', 'retryPayment', { policy: this.policyDetails.slug }, 'Clicked Retry payment button');
    this.router.navigate([`/policy/payment/retry/${this.policyDetails.slug}`]);
  }

  goToClaims() {
    const singleTpa = this.policyDetails.fnolTPAs?.length === 1;
    const multipleTpa = this.policyDetails.fnolTPAs?.length > 1;
    let doMultipleTpaApply = false;

    if (this.policyDetails.fnolTPAs && this.policyDetails.fnolTPAs.length > 0) {
      // doMultipleTpaApply: Does policy have extras that make multiple TPAs relevant for the policy
      doMultipleTpaApply = this.checkPolicyForIncludedTpaExtras();
    }

    if (this.policyDetails.fnolThirdParty) {
      if (this.policyDetails.fnolViaIntercom && this.isIntercomEnabled) {
        // Open Intercom widget
        this.intercomService.show();
      } else if (singleTpa || !doMultipleTpaApply) {
        // Call Handover API to fetch default TPA url
        this.handoverToTPA('default');
      } else if (multipleTpa && doMultipleTpaApply) {
        // Let customer choose claim TPA type on claim triage page
        this.goToClaimTriage();
      } else {
        this.goToSupport('claim');
      }
    } else {
      this.goToSupport('claim');
    }
  }

  goToSupport(subject?: string) {
    const referenceList = this.policyDetails.slug;

    if (!subject) {
      this.router.navigate(['/support']);
    } else {
      this.router.navigate(['/support'], {
        queryParams: {
          action: 'help_flow',
          subject: subject,
          'reference-list': referenceList,
          'product-class': this.policyDetails.productClass,
          'return-url': encodeURIComponent(window.location.href)
        }
      });
    }
  }

  startThirdPartyUrlClaims(url:string) {
    window.open(url, '_blank');
  }

  startIntercomClaims() {
    if (this.isIntercomEnabled) {
      this.intercomService.show();
    }
  }

  handoverToTPA(forCover:string) {
    this.tpaService.handoverToTpa({
      policy_uuid: this.policyDetails.slug,
      for_cover: forCover
    }).then((response: any) => {
      if (response?.url && response?.url !== '') {
        window.open(response?.url, '_blank');
      }
    }
    ).catch((err: any) => {
      this.loggerService.error('PolicyDetailsComponent', 'handoverToTPA', { error: err });
    }
    );
  }

  checkPolicyForIncludedTpaExtras() {
    let numberOfTPAextras = 0;
    for (const tpaItem of this.policyDetails.fnolTPAs) {
      for (const [key, value] of Object.entries<{is_included?: boolean}>(this.policyDetails?.rawExtras || {})) {
        if (tpaItem?.for_cover?.includes(key) && value?.is_included === true) {
            numberOfTPAextras += 1;
        }
      }
    }
    return numberOfTPAextras > 0;
  }

  goToClaimTriage() {
    this.router.navigate([`/policy/${this.policyDetails?.productClass}/${this.policyDetails.slug}/claim-triage`]);
  }
}
